import { UrlLoad } from "@pdfking/compatibility"
import {
  MapAsync,
  ResponseContentLength,
  StreamCreate,
  StreamToIterable,
} from "@pdfking/tfunc"

import type {
  NetworkProgress,
  NetworkTrackingCallback,
} from "../types/network/Network"

/**
 * Fetch with download progress tracking.
 *
 * Wraps UrlLoad to track download progress via callback.
 *
 * @param url - URL to fetch
 * @param options - Progress tracking options with download callback
 * @returns Promise resolving to Response with tracked stream
 *
 * @example
 * ```ts
 * import { ProgressFetch } from "@pdfking/pdfengine"
 *
 * const response = await ProgressFetch("https://example.com/file.pdf", {
 *   download: ({ done, total }) => console.log(`${done}/${total}`)
 * })
 * ```
 */
export const ProgressFetch = (
  url: string,
  { download }: Omit<NetworkProgress, "upload"> = {}
): Promise<Response> => UrlLoad(url).then(r => trackResponse(r, download))

const trackStream = (
  sourceStream: ReadableStream<Uint8Array>,
  progress: NetworkTrackingCallback,
  cancel: () => void,
  content_length?: number
): ReadableStream<Uint8Array> => {
  let done = 0

  const send = () => progress({ done, total: content_length ?? done })

  void send()

  return StreamCreate(
    MapAsync((value: Uint8Array) => {
      done += value.byteLength
      void send()
      return value
    }, StreamToIterable(sourceStream)),
    cancel
  )
}

const trackResponse = (
  response: Response,
  progress?: NetworkTrackingCallback
): Response => {
  if (!progress || !response.body) return response

  return new Response(
    trackStream(
      response.body,
      progress,
      () => response.body!.cancel(),
      ResponseContentLength(response)
    ),
    {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    }
  )
}
